import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createBlock as _createBlock, Fragment as _Fragment, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-1" };
const _hoisted_2 = { class: "flex items-center gap-2" };
const _hoisted_3 = ["id"];
const _hoisted_4 = ["id"];
const _hoisted_5 = { class: "flex flex-col [&>*:last-child]:mb-0" };
const _hoisted_6 = {
    key: 0,
    class: "flex flex-col gap-1 mb-5"
};
const _hoisted_7 = { class: "text-sm font-medium leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_8 = { class: "flex items-center gap-4" };
const _hoisted_9 = { class: "flex flex-col gap-1 mb-5" };
const _hoisted_10 = { class: "text-sm font-medium leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_11 = { class: "flex items-center gap-4" };
const _hoisted_12 = {
    key: 3,
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
import { computed, ref, toRef, unref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Alert, AutoComplete, Button, Modal, PhoneInput, RadioButton, ToggleInput } from '@eudonet/opal';
import { buildAutoCompleteLabels } from '@/shared/composables/autoCompleteLabels';
import { useAddPhoneForm } from '@/shared/composables/channels/useAddPhoneForm';
import { useAppToast } from '@/shared/composables/useAppToast';
import { resolveApiErrorMessage } from '@/infrastructure/services/ApiErrorService';
import { logger } from '@/infrastructure/services/LoggerService';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddPhoneModal',
    props: /*@__PURE__*/ _mergeModels({
        hasPrimaryPhone: { type: Boolean },
        submit: {},
        i18nPrefix: {},
        ownerName: { default: '' },
        withContext: { type: Boolean, default: false },
        organisationSearch: { default: undefined },
        defaultContext: { default: 'Professional' },
        errorOverrides: { default: () => ({}) },
        existingRelationOrgIds: { default: () => [] }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Reusable "Ajouter un numéro" modal (OP-5604 / US-07).
         *
         * Owner-agnostic by design: the persistence call (`submit`), the context mode
         * (`withContext`), the organisation autocomplete (`organisationSearch`) and the
         * i18n namespace (`i18nPrefix`) are all injected by the owner wrapper
         * (`ContactAddPhoneModal`, `OrganisationAddPhoneModal`). Mirrors
         * {@link AddEmailModal}; the email field is swapped for the OPAL `PhoneInput`
         * (country-code selector + E.164 output) and a Mobile/Fixe Type selector is added.
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t, locale } = useI18n();
        const tk = (key, params) => params ? t(`${props.i18nPrefix}.${key}`, params) : t(`${props.i18nPrefix}.${key}`);
        const { toastSuccess } = useAppToast();
        const titleId = useId();
        const hintId = useId();
        const { form, organisationIdError, isProfessional, canSubmit, setPhoneMeta, phoneValidationMessage, normalizePhone, validate, buildPayload, reset, } = useAddPhoneForm({
            hasPrimaryPhone: toRef(props, 'hasPrimaryPhone'),
            withContext: toRef(props, 'withContext'),
            i18nPrefix: toRef(props, 'i18nPrefix'),
            defaultContext: toRef(props, 'defaultContext'),
        });
        // Phone error shown under the input. Gated on `touched` so we don't nag before the
        // user leaves the field. This is the ONLY error source for the field: the modal sets
        // `disable-internal-validation` on PhoneInput, so OPAL's built-in English messages
        // are suppressed and only this localized message is ever rendered.
        const phoneTouched = ref(false);
        const phoneFieldError = computed(() => (phoneTouched.value ? phoneValidationMessage() : ''));
        function onPhoneBlur() {
            phoneTouched.value = true;
            // Collapse separator noise — the typed trunk 0 stays (OP-7665 AC3); stripping to E.164 happens
            // only in buildPayload, on the wire value, never written back into the field.
            normalizePhone();
        }
        const organisationSuggestions = computed(() => unref(props.organisationSearch?.suggestions) ?? []);
        const organisationsLoading = computed(() => unref(props.organisationSearch?.isLoading) ?? false);
        const searchOrgs = (query) => props.organisationSearch?.search(query);
        const clearOrgSearch = () => props.organisationSearch?.clear();
        // Display label of the picked organisation — captured from AutoComplete's `select`
        // event (the suggestions list is cleared on the next search, so we can't re-derive
        // it from `form.organisationId`). Feeds the relation hint.
        const selectedOrganisationName = ref('');
        const submitting = ref(false);
        const serverError = ref(null);
        async function handleSubmit() {
            // Reveal the phone error on submit even if the field was never blurred.
            phoneTouched.value = true;
            if (!validate())
                return;
            submitting.value = true;
            serverError.value = null;
            try {
                await props.submit(buildPayload());
                emit('created');
                reset();
                selectedOrganisationName.value = '';
                phoneTouched.value = false;
                open.value = false;
                toastSuccess(`${props.i18nPrefix}.saveSuccess`);
            }
            catch (error) {
                serverError.value = resolveApiErrorMessage(error, props.errorOverrides, `${props.i18nPrefix}.errors.serverError`);
                logger.error('Failed to add phone', error);
            }
            finally {
                submitting.value = false;
            }
        }
        function handleCancel() {
            reset();
            selectedOrganisationName.value = '';
            phoneTouched.value = false;
            clearOrgSearch();
            serverError.value = null;
            open.value = false;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": [
                    _cache[9] || (_cache[9] = ($event) => ((open).value = $event)),
                    _cache[10] || (_cache[10] = (v) => !v && handleCancel())
                ],
                size: "sm",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "footer-position": "right",
                "aria-labelledby": _unref(titleId),
                "aria-describedby": _unref(hintId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            _cache[11] || (_cache[11] = _createElementVNode("span", {
                                class: "flex h-9 items-center justify-center rounded-lg border border-(--border-border-base-soft) px-3 dark:border-(--border-border-base)",
                                "aria-hidden": "true"
                            }, [
                                _createElementVNode("i", { class: "ri-phone-fill text-xl text-(--text-text-black-default) dark:text-(--text-text-white-default)" })
                            ], -1 /* CACHED */)),
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(tk('title')), 9 /* TEXT, PROPS */, _hoisted_3)
                        ]),
                        _createElementVNode("p", {
                            id: _unref(hintId),
                            class: "text-sm font-normal leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)"
                        }, _toDisplayString(tk('requiredFieldsHint')), 9 /* TEXT, PROPS */, _hoisted_4)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: tk('actions.cancel'),
                        onClick: handleCancel
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: tk('actions.submit'),
                        disabled: !_unref(canSubmit) || submitting.value,
                        loading: submitting.value,
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        (__props.withContext)
                            ? (_openBlock(), _createElementBlock("fieldset", _hoisted_6, [
                                _createElementVNode("legend", _hoisted_7, [
                                    _createTextVNode(_toDisplayString(tk('context.label')) + " ", 1 /* TEXT */),
                                    _cache[12] || (_cache[12] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */))
                                ]),
                                _createElementVNode("div", _hoisted_8, [
                                    _createVNode(_unref(RadioButton), {
                                        modelValue: _unref(form).context,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(form).context) = $event)),
                                        value: 'Professional',
                                        name: "add-phone-context",
                                        label: tk('context.professional')
                                    }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                    _createVNode(_unref(RadioButton), {
                                        modelValue: _unref(form).context,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((_unref(form).context) = $event)),
                                        value: 'Personal',
                                        name: "add-phone-context",
                                        label: tk('context.personal')
                                    }, null, 8 /* PROPS */, ["modelValue", "label"])
                                ])
                            ]))
                            : _createCommentVNode("v-if", true),
                        _createVNode(_unref(PhoneInput), {
                            modelValue: _unref(form).phone,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((_unref(form).phone) = $event)),
                            label: tk('phone.label'),
                            "country-list-label": _unref(t)('common.country'),
                            locale: _unref(locale),
                            error: phoneFieldError.value,
                            required: true,
                            "disable-internal-validation": true,
                            "full-width": "",
                            onBlur: onPhoneBlur,
                            onCountryChange: _cache[3] || (_cache[3] = (country, national) => _unref(setPhoneMeta)(country.dialCode, national))
                        }, null, 8 /* PROPS */, ["modelValue", "label", "country-list-label", "locale", "error"]),
                        _createElementVNode("fieldset", _hoisted_9, [
                            _createElementVNode("legend", _hoisted_10, [
                                _createTextVNode(_toDisplayString(tk('type.label')) + " ", 1 /* TEXT */),
                                _cache[13] || (_cache[13] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */))
                            ]),
                            _createElementVNode("div", _hoisted_11, [
                                _createVNode(_unref(RadioButton), {
                                    modelValue: _unref(form).subType,
                                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((_unref(form).subType) = $event)),
                                    value: 'Mobile',
                                    name: "add-phone-subtype",
                                    label: tk('type.mobile')
                                }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                _createVNode(_unref(RadioButton), {
                                    modelValue: _unref(form).subType,
                                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((_unref(form).subType) = $event)),
                                    value: 'Fixe',
                                    name: "add-phone-subtype",
                                    label: tk('type.fixe')
                                }, null, 8 /* PROPS */, ["modelValue", "label"])
                            ])
                        ]),
                        (_unref(isProfessional))
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                    modelValue: _unref(form).organisationId,
                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((_unref(form).organisationId) = $event)),
                                    label: tk('organisation.label'),
                                    placeholder: tk('organisation.placeholder'),
                                    "empty-label": tk('organisation.empty'),
                                    options: organisationSuggestions.value,
                                    loading: organisationsLoading.value,
                                    error: _unref(organisationIdError),
                                    required: true,
                                    debounce: 250,
                                    "icon-left": "ri-building-line",
                                    "icon-right": "ri-search-line",
                                    "full-width": "",
                                    onSearch: searchOrgs,
                                    onSelect: _cache[7] || (_cache[7] = (opt) => (selectedOrganisationName.value = opt.label)),
                                    onClear: clearOrgSearch
                                }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "empty-label", "options", "loading", "error"]),
                                (_unref(form).organisationId && !__props.existingRelationOrgIds.includes(_unref(form).organisationId))
                                    ? (_openBlock(), _createBlock(_unref(Alert), {
                                        key: 0,
                                        class: "mb-5",
                                        color: "info",
                                        heading: tk('organisation.relationHint', { contact: __props.ownerName, organisation: selectedOrganisationName.value })
                                    }, null, 8 /* PROPS */, ["heading"]))
                                    : _createCommentVNode("v-if", true)
                            ], 64 /* STABLE_FRAGMENT */))
                            : _createCommentVNode("v-if", true),
                        (__props.hasPrimaryPhone)
                            ? (_openBlock(), _createBlock(_unref(ToggleInput), {
                                key: 2,
                                modelValue: _unref(form).isPrimary,
                                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((_unref(form).isPrimary) = $event)),
                                type: "default",
                                class: "mb-5",
                                label: tk('primary')
                            }, null, 8 /* PROPS */, ["modelValue", "label"]))
                            : _createCommentVNode("v-if", true),
                        (serverError.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                _cache[14] || (_cache[14] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]));
        };
    }
});
